import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { startAuthentication } from "@simplewebauthn/browser";
import { api } from "../../lib/peApi";
import { usePEAuth } from "./PEAuthContext";

const inputCls =
  "w-full rounded-sm border border-pe-sand/70 bg-white px-4 py-3 text-sm text-pe-forest outline-none focus:border-pe-gold transition-colors placeholder:text-pe-muted/60";

function Field({ label, required, children, hint }) {
  return (
    <label className="block text-left">
      <span className="block font-sans text-[10px] tracking-[0.2em] uppercase text-pe-olive mb-1.5">
        {label} {required && <span className="text-pe-brass">*</span>}
      </span>
      {children}
      {hint && <span className="block text-[11px] text-pe-muted mt-1">{hint}</span>}
    </label>
  );
}

export function PEAuthCallback() {
  const { setUser } = usePEAuth();
  const navigate = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("session_id");
    api("/auth/google/session", { method: "POST", body: { session_id: sessionId } })
      .then((u) => {
        setUser(u);
        navigate("/prismatic-essence/account", { replace: true });
      })
      .catch(() => navigate("/prismatic-essence/auth?error=google", { replace: true }));
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-pe-ivory flex items-center justify-center">
      <p className="font-pe italic text-xl text-pe-olive">Signing you in…</p>
    </div>
  );
}

export default function PEAuthPage() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("reset") ? "reset" : "login");
  const [form, setForm] = useState({ comms_method: "email", atmosphere: "no-preference" });
  const [error, setError] = useState(params.get("error") === "google" ? "Google sign-in didn't complete. Please try again." : "");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = usePEAuth();
  const navigate = useNavigate();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const go = (u) => {
    setUser(u);
    const back = sessionStorage.getItem("pe_return");
    sessionStorage.removeItem("pe_return");
    navigate(back || "/prismatic-essence/account");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        go(await api("/auth/login", { method: "POST", body: { email: form.email, password: form.password } }));
      } else if (mode === "register") {
        if (form.password !== form.confirm) throw new Error("Passwords don't match.");
        go(await api("/auth/register", { method: "POST", body: { ...form } }));
      } else if (mode === "forgot") {
        await api("/auth/forgot-password", { method: "POST", body: { email: form.email } });
        setNotice("If that email has an account, a reset link is on its way.");
      } else if (mode === "reset") {
        await api("/auth/reset-password", { method: "POST", body: { token: params.get("reset"), password: form.password } });
        setNotice("Password updated. You can sign in now.");
        setMode("login");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/prismatic-essence/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const passkeySignIn = async () => {
    setError("");
    setBusy(true);
    try {
      const optRes = await api("/auth/passkey/login/options", { method: "POST", body: {} });
      const credential = await startAuthentication({ optionsJSON: JSON.parse(optRes.options) });
      const u = await api("/auth/passkey/login/verify", { method: "POST", body: { state_id: optRes.state_id, credential } });
      go(u);
    } catch (e) {
      setError(e.name === "NotAllowedError" ? "Passkey sign-in was cancelled." : e.message || "Passkey sign-in failed on this device.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main id="main-content" className="min-h-screen bg-pe-ivory py-28 px-5">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-10">
          <Link to="/prismatic-essence"><img src="/images/pe-logo.png" alt="Prismatic Essence" className="h-16 w-auto mx-auto mb-4" /></Link>
          <h1 className="font-pe text-3xl text-pe-forest">{mode === "register" ? "Create your account" : mode === "login" ? "Welcome back" : mode === "forgot" ? "Reset your password" : "Choose a new password"}</h1>
          <p className="font-pe italic text-pe-brass mt-2">Beauty in Every Dimension.</p>
        </div>

        <div data-testid="pe-access-explainer" className="mb-6 rounded-sm border border-pe-sand/60 bg-pe-cream/60 p-5 text-[12px] text-pe-body leading-relaxed space-y-1.5">
          <p><strong className="text-pe-forest">Account</strong> — email and password; your dashboard holds forms, preferences, rewards and appointments.</p>
          <p><strong className="text-pe-forest">Passkey</strong> — sign in with your device's face, fingerprint or PIN; no password to remember, on devices that support it.</p>
          <p><strong className="text-pe-forest">Guest</strong> — book without an account and manage your appointment with a secure one-time code sent to your email. <Link to="/prismatic-essence/guest" className="text-pe-brass underline underline-offset-4">Guest access</Link></p>
        </div>

        <form data-testid="pe-auth-form" onSubmit={submit} className="bg-white border border-pe-sand/60 rounded-sm p-7 md:p-9 space-y-5 shadow-[0_16px_50px_rgba(44,58,40,0.07)]">
          {error && <p data-testid="pe-auth-error" className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-sm px-4 py-3">{error}</p>}
          {notice && <p data-testid="pe-auth-notice" className="text-sm text-pe-forest bg-pe-cream border border-pe-gold/40 rounded-sm px-4 py-3">{notice}</p>}

          {(mode === "login" || mode === "register" || mode === "forgot") && (
            <Field label="Email address" required>
              <input data-testid="pe-auth-email" type="email" required className={inputCls} onChange={set("email")} />
            </Field>
          )}

          {mode === "register" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Full name" required>
                  <input data-testid="pe-auth-name" required className={inputCls} onChange={set("name")} />
                </Field>
                <Field label="Preferred name">
                  <input className={inputCls} onChange={set("preferred_name")} />
                </Field>
              </div>
              <Field label="Mobile number" required>
                <input data-testid="pe-auth-phone" type="tel" required className={inputCls} placeholder="+1 …" onChange={set("phone")} />
              </Field>
              <Field label="Preferred communication" required>
                <select className={inputCls} onChange={set("comms_method")} defaultValue="email">
                  <option value="email">Email</option>
                  <option value="sms">Text message</option>
                  <option value="either">Either is fine</option>
                </select>
              </Field>
              <Field label="Referral code (optional)">
                <input className={inputCls} placeholder="PE-XXXXXX" onChange={set("referral_code")} />
              </Field>
            </>
          )}

          {(mode === "login" || mode === "register" || mode === "reset") && (
            <Field label={mode === "reset" ? "New password" : "Password"} required hint="At least 8 characters, with a letter and a number.">
              <input data-testid="pe-auth-password" type="password" required minLength={8} className={inputCls} onChange={set("password")} />
            </Field>
          )}
          {mode === "register" && (
            <Field label="Confirm password" required>
              <input type="password" required className={inputCls} onChange={set("confirm")} />
            </Field>
          )}

          {mode === "register" && (
            <>
              <label className="flex items-start gap-3 text-[13px] text-pe-body leading-relaxed">
                <input data-testid="pe-auth-privacy" type="checkbox" required className="mt-1 accent-[#C59B27]" onChange={set("privacy_ack")} />
                <span>I have read and understand the privacy notice: my information is used only to schedule and prepare my appointments, communicate about my services, process my deposit, manage my account and rewards, and keep my experience safe. It is never sold or shared for third-party marketing. <span className="text-pe-muted">(Full privacy policy is being finalized before launch.)</span></span>
              </label>
              <label className="flex items-start gap-3 text-[13px] text-pe-body">
                <input type="checkbox" className="mt-1 accent-[#C59B27]" onChange={set("marketing_opt_in")} />
                <span>Send me occasional news and offers (optional — appointment communications still arrive either way).</span>
              </label>
            </>
          )}

          <button data-testid="pe-auth-submit" disabled={busy} className="w-full rounded-full bg-pe-forest text-pe-ivory py-3.5 font-sans text-[11px] tracking-[0.25em] uppercase hover:bg-pe-olive transition-colors disabled:opacity-50">
            {busy ? "One moment…" : mode === "login" ? "Sign in" : mode === "register" ? "Create account" : mode === "forgot" ? "Send reset link" : "Update password"}
          </button>

          {(mode === "login" || mode === "register") && (
            <>
              <div className="flex items-center gap-4"><span className="flex-1 h-px bg-pe-sand/60" /><span className="text-[10px] tracking-[0.2em] uppercase text-pe-muted">or</span><span className="flex-1 h-px bg-pe-sand/60" /></div>
              <button type="button" data-testid="pe-auth-google" onClick={googleSignIn} className="w-full rounded-full border border-pe-brass/60 text-pe-forest py-3.5 font-sans text-[11px] tracking-[0.25em] uppercase hover:bg-pe-gold/10 transition-colors">
                Continue with Google
              </button>
              <button type="button" data-testid="pe-auth-passkey" onClick={passkeySignIn} disabled={busy} className="w-full rounded-full border border-pe-brass/60 text-pe-forest py-3.5 font-sans text-[11px] tracking-[0.25em] uppercase hover:bg-pe-gold/10 transition-colors disabled:opacity-50">
                Sign in with a passkey
              </button>
              <p className="text-[11px] text-pe-muted text-center">Passkeys work on devices that support face, fingerprint or PIN sign-in.</p>
            </>
          )}

          <div className="flex justify-between text-[12px] text-pe-olive pt-1">
            {mode !== "login" && <button type="button" onClick={() => setMode("login")} className="underline underline-offset-4">Sign in</button>}
            {mode === "login" && (
              <>
                <button type="button" data-testid="pe-auth-forgot-link" onClick={() => setMode("forgot")} className="underline underline-offset-4">Forgot password?</button>
                <button type="button" data-testid="pe-auth-register-link" onClick={() => setMode("register")} className="underline underline-offset-4">Create account</button>
              </>
            )}
          </div>
        </form>

        <p className="text-center mt-8">
          <Link to="/prismatic-essence" className="text-[11px] tracking-[0.2em] uppercase text-pe-muted hover:text-pe-forest">← Back to Prismatic Essence</Link>
        </p>
      </div>
    </main>
  );
}
